/**
 * Schema helpers.
 *
 * Implements the first batch of schema utilities for Phase 4 (issue #205):
 *   - `defineTable(moduleId, name, columns)` — auto-prefix with `{module_id}_`
 *     enforcing the DB naming convention at build time. See
 *     `docs/DB_CONVENTIONS.md` for the rules.
 *   - `crossModuleFk(ownerModuleId, tableName, columnName?)` — declare a
 *     cross-module foreign key reference by resolving to the prefixed table
 *     name. The SDK does not validate target existence at compile time —
 *     that happens at runtime during install (Phase 5).
 *
 * Still stubbed (follow-up commits):
 *   - `defineEnum()` — string literal enum helpers shared across FE/BE via zod.
 *   - `extendSchema()` — typed version of `extendSchemas` on `ModuleConfig`.
 */

import { sqliteTable } from "drizzle-orm/sqlite-core";
import type {
  SQLiteColumnBuilderBase,
  SQLiteTableWithColumns,
  SQLiteTableExtraConfigValue,
} from "drizzle-orm/sqlite-core";
import type { BuildColumns } from "drizzle-orm/column-builder";

// ============================================================================
// Module ID / table name validation
// ============================================================================

/**
 * Canonical module id format: lowercase snake_case, must start with a letter.
 * See `docs/DB_CONVENTIONS.md` §"Module ID Rules".
 */
const MODULE_ID_REGEX = /^[a-z][a-z0-9_]*$/;

/**
 * Table name (without module prefix) must also be snake_case and start with
 * a letter. Leading underscores and uppercase are rejected.
 */
const TABLE_NAME_REGEX = /^[a-z][a-z0-9_]*$/;

/**
 * Normalise a module id to its canonical DB-safe form. The module system
 * accepts both `car-rental` (kebab, used in package names / filesystem) and
 * `car_rental` (snake, used in DB). Internally the DB layer always uses the
 * snake form, so we convert hyphens here.
 */
function normaliseModuleId(moduleId: string): string {
  return moduleId.replace(/-/g, "_");
}

function assertValidModuleId(moduleId: string, normalised: string): void {
  if (!moduleId || typeof moduleId !== "string") {
    throw new Error(
      `[@kubdee/sdk] defineTable: moduleId is required and must be a string (got ${String(moduleId)})`,
    );
  }
  if (!MODULE_ID_REGEX.test(normalised)) {
    throw new Error(
      `[@kubdee/sdk] defineTable: invalid moduleId "${moduleId}". ` +
        `Must be snake_case, start with a letter, and contain only [a-z0-9_]. ` +
        `See docs/DB_CONVENTIONS.md for the full rules.`,
    );
  }
}

function assertValidTableName(moduleIdSnake: string, tableName: string): void {
  if (!tableName || typeof tableName !== "string") {
    throw new Error(
      `[@kubdee/sdk] defineTable: tableName is required and must be a string (got ${String(tableName)})`,
    );
  }
  if (!TABLE_NAME_REGEX.test(tableName)) {
    throw new Error(
      `[@kubdee/sdk] defineTable: invalid tableName "${tableName}". ` +
        `Must be snake_case, start with a letter, and contain only [a-z0-9_].`,
    );
  }
  const prefix = `${moduleIdSnake}_`;
  if (tableName.startsWith(prefix)) {
    throw new Error(
      `[@kubdee/sdk] defineTable: tableName "${tableName}" must not start ` +
        `with the module id prefix "${prefix}" — the SDK will add it ` +
        `automatically. Use "${tableName.slice(prefix.length)}" instead.`,
    );
  }
}

// ============================================================================
// defineTable
// ============================================================================

/**
 * Create a Drizzle SQLite table whose actual name is prefixed by the module's
 * canonical id. Enforces the DB naming convention at the point of definition.
 *
 * @example
 *   // modules/car-rental/schemas/bookings.ts
 *   export const bookings = defineTable('car-rental', 'bookings', {
 *     id: text('id').primaryKey(),
 *     customerId: text('customer_id').notNull(),
 *   })
 *   // → actual table name: "car_rental_bookings"
 *
 * @param moduleId   Canonical module id (accepts kebab or snake form; both
 *                   are normalised to snake_case).
 * @param tableName  Table name without prefix. MUST NOT start with the
 *                   module id prefix.
 * @param columns    Drizzle column builders, same shape as `sqliteTable`.
 * @param extraConfig Optional extra config (indexes, FKs, unique, checks) —
 *                   forwarded to Drizzle.
 */
export function defineTable<
  TTableBase extends string,
  TColumnsMap extends Record<string, SQLiteColumnBuilderBase>,
>(
  moduleId: string,
  tableName: TTableBase,
  columns: TColumnsMap,
  extraConfig?: (
    self: BuildColumns<string, TColumnsMap, "sqlite">,
  ) => SQLiteTableExtraConfigValue[],
): SQLiteTableWithColumns<{
  name: string;
  schema: undefined;
  columns: BuildColumns<string, TColumnsMap, "sqlite">;
  dialect: "sqlite";
}> {
  const moduleIdSnake = normaliseModuleId(moduleId);
  assertValidModuleId(moduleId, moduleIdSnake);
  assertValidTableName(moduleIdSnake, tableName);

  const fullName = `${moduleIdSnake}_${tableName}`;

  // Drizzle's runtime `sqliteTable` accepts any string for the name; we
  // widen the generic so the prefixed name type-checks against
  // `SQLiteTableWithColumns`.
  return sqliteTable(fullName, columns, extraConfig) as SQLiteTableWithColumns<{
    name: string;
    schema: undefined;
    columns: BuildColumns<string, TColumnsMap, "sqlite">;
    dialect: "sqlite";
  }>;
}

// ============================================================================
// crossModuleFk
// ============================================================================

/**
 * Resolve a cross-module foreign key reference.
 *
 * This is a pure helper — it returns the fully-qualified table name and
 * column name for a table owned by another module. Consumers typically use
 * the result inside a Drizzle `references()` callback, or to look up a
 * column on an imported schema.
 *
 * The SDK does **not** verify at compile time that the target table exists
 * — that check runs at runtime during module install (Phase 5, see
 * `docs/DB_CONVENTIONS.md` §"Cross-Module References").
 *
 * @example
 *   import { crossModuleFk } from '@kubdee/sdk/schema'
 *
 *   const ref = crossModuleFk('core', 'customers', 'id')
 *   // → { tableName: 'core_customers', columnName: 'id' }
 */
export function crossModuleFk(
  ownerModuleId: string,
  tableName: string,
  columnName: string = "id",
): { tableName: string; columnName: string } {
  const ownerSnake = normaliseModuleId(ownerModuleId);
  assertValidModuleId(ownerModuleId, ownerSnake);
  assertValidTableName(ownerSnake, tableName);

  if (!columnName || typeof columnName !== "string") {
    throw new Error(
      `[@kubdee/sdk] crossModuleFk: columnName must be a non-empty string (got ${String(columnName)})`,
    );
  }

  return {
    tableName: `${ownerSnake}_${tableName}`,
    columnName,
  };
}

// ============================================================================
// validateMigrationSQL (Phase 5 Batch 2A-1 — #206)
// ============================================================================

/**
 * Result returned by {@link validateMigrationSQL}.
 */
export interface MigrationSqlValidationResult {
  /** `true` if the statement only touches tables owned by the module. */
  ok: boolean;
  /** Human-readable reason when `ok` is `false`. */
  reason?: string;
  /** The first captured table name that violated the prefix rule. */
  offendingTable?: string;
}

/**
 * Patterns used by {@link validateMigrationSQL}. Each entry captures one or
 * more table names in the SQL statement. Every captured name must start with
 * the caller module's `{module_id_snake}_` prefix.
 *
 * The patterns are intentionally conservative — they target the DDL/DML
 * shapes that could possibly mutate cross-module state:
 *
 *   CREATE [TEMP|TEMPORARY] TABLE [IF NOT EXISTS] <name>
 *   DROP TABLE [IF EXISTS] <name>
 *   ALTER TABLE <name>
 *   ALTER TABLE <old> RENAME TO <new>          (both captured)
 *   CREATE [UNIQUE] INDEX [IF NOT EXISTS] [idx] ON <name>
 *   DROP INDEX [IF EXISTS] <idx> ON <name>
 *   CREATE TRIGGER [IF NOT EXISTS] <name> ... ON <table>
 *   DROP TRIGGER [IF EXISTS] <name>            (no table — allowed)
 *   CREATE [TEMP] VIEW [IF NOT EXISTS] <view> AS SELECT ... FROM <table>
 *   DROP VIEW [IF EXISTS] <view>
 *   INSERT [OR ...] INTO <name>
 *   UPDATE <name>
 *   DELETE FROM <name>
 *
 * Unknown shapes (PRAGMA, BEGIN, COMMIT, ANALYZE, VACUUM, …) pass through
 * with a debug log — the goal is "catch the dangerous cases", not to parse
 * SQLite exhaustively.
 *
 * Regex notes:
 *   - `/is` flags: case-insensitive + dotall (so `.` matches newlines for
 *     the TRIGGER pattern which may span lines).
 *   - `["`]?` allows optional identifier quoting with `"` or backticks.
 *   - `[a-z0-9_]+` matches the normalised snake_case identifier body.
 *   - We intentionally do NOT try to match schema-qualified names
 *     (`main.foo`) — the convention uses a single unqualified namespace.
 */
const VALIDATOR_PATTERNS: ReadonlyArray<{
  label: string;
  regex: RegExp;
  /**
   * Given a match result, return the captured table names to check. Any
   * name returned here must start with the module prefix.
   */
  extract: (m: RegExpMatchArray) => string[];
}> = [
  // ALTER TABLE <old> RENAME TO <new> — both must be module-owned. Check
  // BEFORE the plain ALTER TABLE pattern so we capture both names.
  {
    label: "ALTER TABLE ... RENAME TO",
    regex:
      /\bALTER\s+TABLE\s+["`]?([a-z0-9_]+)["`]?\s+RENAME\s+TO\s+["`]?([a-z0-9_]+)["`]?/i,
    extract: (m) => [m[1], m[2]],
  },
  // CREATE [TEMP|TEMPORARY] TABLE [IF NOT EXISTS] <name>
  {
    label: "CREATE TABLE",
    regex:
      /\bCREATE\s+(?:TEMP\s+|TEMPORARY\s+)?TABLE\s+(?:IF\s+NOT\s+EXISTS\s+)?["`]?([a-z0-9_]+)["`]?/i,
    extract: (m) => [m[1]],
  },
  // DROP TABLE [IF EXISTS] <name>
  {
    label: "DROP TABLE",
    regex: /\bDROP\s+TABLE\s+(?:IF\s+EXISTS\s+)?["`]?([a-z0-9_]+)["`]?/i,
    extract: (m) => [m[1]],
  },
  // ALTER TABLE <name>  (plain — ADD/DROP/MODIFY COLUMN etc.)
  {
    label: "ALTER TABLE",
    regex: /\bALTER\s+TABLE\s+["`]?([a-z0-9_]+)["`]?/i,
    extract: (m) => [m[1]],
  },
  // CREATE [UNIQUE] INDEX [IF NOT EXISTS] [idx_name] ON <name>
  {
    label: "CREATE INDEX",
    regex:
      /\bCREATE\s+(?:UNIQUE\s+)?INDEX\s+(?:IF\s+NOT\s+EXISTS\s+)?(?:["`]?[a-z0-9_]+["`]?\s+)?ON\s+["`]?([a-z0-9_]+)["`]?/i,
    extract: (m) => [m[1]],
  },
  // DROP INDEX [IF EXISTS] <idx> ON <name>  (non-standard SQLite but cover)
  {
    label: "DROP INDEX ... ON",
    regex:
      /\bDROP\s+INDEX\s+(?:IF\s+EXISTS\s+)?["`]?[a-z0-9_]+["`]?\s+ON\s+["`]?([a-z0-9_]+)["`]?/i,
    extract: (m) => [m[1]],
  },
  // CREATE TRIGGER [IF NOT EXISTS] <name> ... ON <table>  (multiline)
  {
    label: "CREATE TRIGGER",
    regex:
      /\bCREATE\s+(?:TEMP\s+|TEMPORARY\s+)?TRIGGER\s+(?:IF\s+NOT\s+EXISTS\s+)?["`]?[a-z0-9_]+["`]?[\s\S]*?\bON\s+["`]?([a-z0-9_]+)["`]?/i,
    extract: (m) => [m[1]],
  },
  // CREATE [TEMP] VIEW [IF NOT EXISTS] <view> AS SELECT ... FROM <table>
  // Both the view name AND the underlying source table must be module-owned.
  {
    label: "CREATE VIEW",
    regex:
      /\bCREATE\s+(?:TEMP\s+|TEMPORARY\s+)?VIEW\s+(?:IF\s+NOT\s+EXISTS\s+)?["`]?([a-z0-9_]+)["`]?[\s\S]*?\bFROM\s+["`]?([a-z0-9_]+)["`]?/i,
    extract: (m) => [m[1], m[2]],
  },
  // DROP VIEW [IF EXISTS] <view>
  {
    label: "DROP VIEW",
    regex: /\bDROP\s+VIEW\s+(?:IF\s+EXISTS\s+)?["`]?([a-z0-9_]+)["`]?/i,
    extract: (m) => [m[1]],
  },
  // INSERT [OR ABORT|FAIL|IGNORE|REPLACE|ROLLBACK] INTO <name>
  {
    label: "INSERT INTO",
    regex:
      /\bINSERT\s+(?:OR\s+(?:ABORT|FAIL|IGNORE|REPLACE|ROLLBACK)\s+)?INTO\s+["`]?([a-z0-9_]+)["`]?/i,
    extract: (m) => [m[1]],
  },
  // UPDATE <name>
  {
    label: "UPDATE",
    regex: /\bUPDATE\s+["`]?([a-z0-9_]+)["`]?/i,
    extract: (m) => [m[1]],
  },
  // DELETE FROM <name>
  {
    label: "DELETE FROM",
    regex: /\bDELETE\s+FROM\s+["`]?([a-z0-9_]+)["`]?/i,
    extract: (m) => [m[1]],
  },
];

/**
 * Validate that a raw SQL statement from a module migration only touches
 * tables owned by that module (i.e. prefixed with `{module_id_snake}_`).
 *
 * This is a STRING-level safety net — it is not a real SQL parser. It runs
 * a handful of conservative regexes against the statement, extracts every
 * table name they can see, and rejects the statement if any captured name
 * does not start with the module prefix.
 *
 * Cross-module references (e.g. an `INSERT INTO core_users` from a
 * `car_rental` migration) are REJECTED at this layer — they should be
 * expressed through {@link crossModuleFk} declarations and handled by the
 * owning module's own migrations, not side-stepped in another module's SQL.
 *
 * Unknown / unmatched shapes (`PRAGMA foreign_keys = ON`, `BEGIN`, raw
 * `VACUUM`, etc.) pass through with `{ ok: true }`. The goal is "catch the
 * dangerous cases", not to parse SQLite exhaustively.
 *
 * @param moduleId   Canonical module id (accepts kebab or snake — normalised).
 * @param sql        A single raw SQL statement string.
 */
export function validateMigrationSQL(
  moduleId: string,
  sql: string,
): MigrationSqlValidationResult {
  const moduleIdSnake = normaliseModuleId(moduleId);
  assertValidModuleId(moduleId, moduleIdSnake);

  if (typeof sql !== "string") {
    return {
      ok: false,
      reason: `[@kubdee/sdk] validateMigrationSQL: sql must be a string (got ${typeof sql})`,
    };
  }

  const prefix = `${moduleIdSnake}_`;

  // Strip line + block comments so the regexes don't get tripped up by
  // narrative text that happens to contain keywords like "UPDATE".
  const stripped = sql
    .replace(/--[^\n]*/g, " ")
    .replace(/\/\*[\s\S]*?\*\//g, " ");

  for (const pattern of VALIDATOR_PATTERNS) {
    const match = stripped.match(pattern.regex);
    if (!match) continue;

    const tables = pattern.extract(match);
    for (const table of tables) {
      if (!table) continue;
      if (!table.startsWith(prefix)) {
        return {
          ok: false,
          offendingTable: table,
          reason:
            `[@kubdee/sdk] validateMigrationSQL: ${pattern.label} touches ` +
            `table "${table}" which is not owned by module "${moduleIdSnake}" ` +
            `(expected prefix "${prefix}"). Cross-module writes must go ` +
            `through the owning module's own migrations — see ` +
            `docs/DB_CONVENTIONS.md §"Cross-Module References".`,
        };
      }
    }

    // Pattern matched and everything was module-owned → accept.
    return { ok: true };
  }

  // No pattern matched — unknown shape, let it through.
  return { ok: true };
}

// ============================================================================
// Module migrations (Phase 5 foundation — #206)
// ============================================================================

/**
 * Semver-lite: we only accept `MAJOR.MINOR.PATCH` without pre-release or build
 * metadata. This keeps version ordering cheap (numeric tuple compare) while
 * still matching what `defineModule({ version })` emits.
 */
const SEMVER_LITE_REGEX = /^\d+\.\d+\.\d+$/;

/**
 * A versioned, forward-only migration for a single module.
 *
 * Each entry represents one schema change (e.g. `0.1.0` — initial tables,
 * `0.2.0` — add a column, etc.). Callers supply raw SQL strings for the
 * foundation phase; a future follow-up may accept Drizzle builder fragments
 * so migrations can be type-checked against the module's schema.
 */
export interface ModuleMigration {
  /** Semver-lite tag `MAJOR.MINOR.PATCH`. Must be unique per module. */
  version: string;
  /** Human-readable summary — surfaces in logs, audit trail, and errors. */
  description: string;
  /** Forward SQL statements, executed in order. Must be a non-empty array. */
  up: readonly string[];
  /**
   * Optional rollback SQL statements (reverse order).
   *
   * The foundation runner is forward-only and does not execute `down`. It is
   * included here so module authors can document reversals for manual
   * recovery. Will be wired up in a later phase.
   */
  down?: readonly string[];
}

/**
 * Runtime context required to run migrations. Uses the minimal D1-like
 * shape so the SDK stays framework-agnostic: anything that can `exec` a
 * raw SQL statement and `query` with bound params will work.
 *
 * D1 satisfies this with a thin adapter (see `applyModuleMigrations` docs).
 */
export interface MigrationRunContext {
  /**
   * Execute one or more SQL statements with no result. Implementations
   * should accept a single raw SQL string and throw on failure.
   */
  exec(sql: string): Promise<unknown>;
  /**
   * Execute a parameterised SQL query and return matching rows.
   *
   * @param sql    SQL template using `?` placeholders.
   * @param params Bound parameters, in positional order.
   */
  query<T = unknown>(
    sql: string,
    params?: readonly unknown[],
  ): Promise<T[]>;
}

/**
 * Compare two semver-lite strings. Returns `-1 | 0 | 1` like `localeCompare`.
 * Both inputs must already match `SEMVER_LITE_REGEX`; the callers in this
 * module enforce that at define time so we can parse without re-validating.
 */
function compareSemverLite(a: string, b: string): number {
  const [aMajor, aMinor, aPatch] = a.split(".").map((n) => Number(n));
  const [bMajor, bMinor, bPatch] = b.split(".").map((n) => Number(n));
  if (aMajor !== bMajor) return aMajor < bMajor ? -1 : 1;
  if (aMinor !== bMinor) return aMinor < bMinor ? -1 : 1;
  if (aPatch !== bPatch) return aPatch < bPatch ? -1 : 1;
  return 0;
}

function assertValidMigration(
  moduleId: string,
  migration: ModuleMigration,
  index: number,
): void {
  if (!migration || typeof migration !== "object") {
    throw new Error(
      `[@kubdee/sdk] defineMigrations("${moduleId}"): migration at index ${index} must be an object`,
    );
  }
  if (typeof migration.version !== "string" || !migration.version) {
    throw new Error(
      `[@kubdee/sdk] defineMigrations("${moduleId}"): migration at index ${index} is missing a "version" string`,
    );
  }
  if (!SEMVER_LITE_REGEX.test(migration.version)) {
    throw new Error(
      `[@kubdee/sdk] defineMigrations("${moduleId}"): invalid version "${migration.version}" at index ${index}. ` +
        `Must match MAJOR.MINOR.PATCH (e.g. "0.1.0", "1.2.3").`,
    );
  }
  if (
    typeof migration.description !== "string" ||
    migration.description.trim().length === 0
  ) {
    throw new Error(
      `[@kubdee/sdk] defineMigrations("${moduleId}"): migration "${migration.version}" is missing a non-empty "description"`,
    );
  }
  if (!Array.isArray(migration.up) || migration.up.length === 0) {
    throw new Error(
      `[@kubdee/sdk] defineMigrations("${moduleId}"): migration "${migration.version}" must have a non-empty "up" array`,
    );
  }
  migration.up.forEach((stmt, stmtIndex) => {
    if (typeof stmt !== "string" || stmt.trim().length === 0) {
      throw new Error(
        `[@kubdee/sdk] defineMigrations("${moduleId}"): migration "${migration.version}" "up"[${stmtIndex}] must be a non-empty SQL string`,
      );
    }
    const check = validateMigrationSQL(moduleId, stmt);
    if (!check.ok) {
      throw new Error(
        `[@kubdee/sdk] defineMigrations("${moduleId}"): migration "${migration.version}" "up"[${stmtIndex}] ` +
          `references table outside module namespace — ${check.reason}`,
      );
    }
  });
  if (migration.down !== undefined) {
    if (!Array.isArray(migration.down)) {
      throw new Error(
        `[@kubdee/sdk] defineMigrations("${moduleId}"): migration "${migration.version}" "down" must be an array when provided`,
      );
    }
    migration.down.forEach((stmt, stmtIndex) => {
      if (typeof stmt !== "string" || stmt.trim().length === 0) {
        throw new Error(
          `[@kubdee/sdk] defineMigrations("${moduleId}"): migration "${migration.version}" "down"[${stmtIndex}] must be a non-empty SQL string`,
        );
      }
      const check = validateMigrationSQL(moduleId, stmt);
      if (!check.ok) {
        throw new Error(
          `[@kubdee/sdk] defineMigrations("${moduleId}"): migration "${migration.version}" "down"[${stmtIndex}] ` +
            `references table outside module namespace — ${check.reason}`,
        );
      }
    });
  }
}

/**
 * Declare a module's ordered migration list.
 *
 * Validates at define time:
 *   - `moduleId` matches the DB convention
 *   - Versions are unique within the list
 *   - Each migration has a non-empty `up` array of non-empty SQL strings
 *   - Each `version` is semver-lite `MAJOR.MINOR.PATCH`
 *
 * Returned array is frozen so callers can't mutate it in place.
 *
 * TODO(#206): a follow-up layer should also validate that every statement
 * in `up` only touches tables matching `{moduleId}_*` (currently we trust
 * the module author).
 *
 * @example
 *   export const migrations = defineMigrations('car_rental', [
 *     {
 *       version: '0.1.0',
 *       description: 'Initial bookings table',
 *       up: [
 *         `CREATE TABLE car_rental_bookings (
 *            id TEXT PRIMARY KEY,
 *            customer_id TEXT NOT NULL
 *          )`,
 *       ],
 *     },
 *   ])
 */
export function defineMigrations(
  moduleId: string,
  migrations: readonly ModuleMigration[],
): readonly ModuleMigration[] {
  const moduleIdSnake = normaliseModuleId(moduleId);
  assertValidModuleId(moduleId, moduleIdSnake);

  if (!Array.isArray(migrations)) {
    throw new Error(
      `[@kubdee/sdk] defineMigrations("${moduleId}"): migrations must be an array`,
    );
  }

  const seenVersions = new Set<string>();
  migrations.forEach((migration, index) => {
    assertValidMigration(moduleIdSnake, migration, index);
    if (seenVersions.has(migration.version)) {
      throw new Error(
        `[@kubdee/sdk] defineMigrations("${moduleId}"): duplicate version "${migration.version}" at index ${index}`,
      );
    }
    seenVersions.add(migration.version);
  });

  // Freeze so callers can't mutate; still `readonly ModuleMigration[]` in the
  // type signature so TS callers get the same guarantee.
  return Object.freeze(migrations.slice()) as readonly ModuleMigration[];
}

/**
 * Apply any pending migrations for a module against a single tenant's DB.
 *
 * Behavior:
 *   1. Reads `installed_modules` for `(tenantId, moduleId)` to determine the
 *      current version. If no row exists, `currentVersion = "0.0.0"`.
 *   2. Filters `migrations` to those strictly greater than `currentVersion`
 *      and sorts ascending by semver.
 *   3. For each pending migration, runs every `up` statement in order via
 *      `ctx.exec`, then UPSERTs `installed_modules` with the new version
 *      and `updatedAt = now`.
 *   4. Returns `{ applied, skipped }` where both arrays are the affected
 *      version strings (in ascending order).
 *
 * **D1 transaction caveat:** Cloudflare D1 does not support multi-statement
 * transactions across separate `exec()` calls. We execute statements
 * sequentially and rely on the per-migration version bump to record the
 * farthest point of progress. If a statement fails mid-migration, the
 * partial state is NOT rolled back — callers should author idempotent
 * `up` scripts (use `IF NOT EXISTS`, etc.) and/or write smaller migrations.
 *
 * Foundation scope: operates on a single tenant's DB context. Multi-tenant
 * orchestration (iterating across all tenants) lives in the dispatch worker
 * and is out of scope for this commit.
 */
export async function applyModuleMigrations(
  ctx: MigrationRunContext,
  params: {
    tenantId: string;
    moduleId: string;
    migrations: readonly ModuleMigration[];
  },
): Promise<{ applied: string[]; skipped: string[] }> {
  const { tenantId, moduleId, migrations } = params;

  if (!ctx || typeof ctx.exec !== "function" || typeof ctx.query !== "function") {
    throw new Error(
      `[@kubdee/sdk] applyModuleMigrations: ctx must implement { exec, query }`,
    );
  }
  if (!tenantId || typeof tenantId !== "string") {
    throw new Error(
      `[@kubdee/sdk] applyModuleMigrations: tenantId is required`,
    );
  }

  const moduleIdSnake = normaliseModuleId(moduleId);
  assertValidModuleId(moduleId, moduleIdSnake);

  if (!Array.isArray(migrations)) {
    throw new Error(
      `[@kubdee/sdk] applyModuleMigrations: migrations must be an array`,
    );
  }

  if (migrations.length === 0) {
    return { applied: [], skipped: [] };
  }

  // Sort ascending — callers usually author in order but we don't trust it.
  const sorted = migrations.slice().sort((a, b) =>
    compareSemverLite(a.version, b.version),
  );

  // 1. Read current installed version
  const rows = await ctx.query<{ version: string }>(
    `SELECT version FROM installed_modules WHERE tenant_id = ? AND module_id = ? LIMIT 1`,
    [tenantId, moduleIdSnake],
  );
  const hasExistingRow = rows.length > 0;
  const currentVersion = hasExistingRow ? rows[0].version : "0.0.0";

  const applied: string[] = [];
  const skipped: string[] = [];

  for (const migration of sorted) {
    if (compareSemverLite(migration.version, currentVersion) <= 0) {
      skipped.push(migration.version);
      continue;
    }

    // Run every up statement sequentially (see D1 caveat above).
    // Runtime belt-and-braces: re-validate each statement just before exec
    // in case this migration list was assembled programmatically without
    // going through defineMigrations().
    for (const stmt of migration.up) {
      const check = validateMigrationSQL(moduleIdSnake, stmt);
      if (!check.ok) {
        throw new Error(
          `[@kubdee/sdk] applyModuleMigrations("${moduleIdSnake}"): migration "${migration.version}" ` +
            `refused at runtime — ${check.reason}`,
        );
      }
      await ctx.exec(stmt);
    }

    // 2. Upsert version row. We INSERT OR REPLACE on (tenant_id, module_id).
    // The installed_modules table uses a ULID as primary key, so we cannot
    // rely on ON CONFLICT against the surrogate key; instead we do a manual
    // read-then-write here. Because only a single worker should be running
    // migrations for a tenant at a time, this is safe.
    const nowIso = new Date().toISOString();
    const existing = await ctx.query<{ id: string }>(
      `SELECT id FROM installed_modules WHERE tenant_id = ? AND module_id = ? LIMIT 1`,
      [tenantId, moduleIdSnake],
    );

    if (existing.length > 0) {
      await ctx.query(
        `UPDATE installed_modules
           SET version = ?, updated_at = ?
         WHERE tenant_id = ? AND module_id = ?`,
        [migration.version, nowIso, tenantId, moduleIdSnake],
      );
    } else {
      // Synthesise a simple id — callers that want a ULID can wire an
      // alternative runner. A deterministic form keeps tests reproducible.
      const newId = `im_${tenantId}_${moduleIdSnake}`;
      await ctx.query(
        `INSERT INTO installed_modules
           (id, tenant_id, module_id, version, enabled, installed_at, updated_at)
         VALUES (?, ?, ?, ?, 1, ?, ?)`,
        [newId, tenantId, moduleIdSnake, migration.version, nowIso, nowIso],
      );
    }

    applied.push(migration.version);
  }

  return { applied, skipped };
}
